import {
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
} from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import Add from "@mui/icons-material/Add";
import Close from "@mui/icons-material/Close";
import { Fragment, useState } from "react";
import {
  EmailNotificationCreateSection,
  EmailNotificationMoreInfoSection,
} from "@dmca/frontend/widgets/admin";
import {
  IEmailReportEnum,
  IEmailReportEnumNameMap,
} from "@dmca/models/interface/email-report";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@dmca/frontend/components/button";

export const Route = createFileRoute("/_authed/_drawerLayout/admin/notification/email")(
  {
    component: RouteComponent,
    loader: async ({ context: { qc, trpcQcProxy } }) => {
      qc.prefetchQuery(trpcQcProxy.user.getAll.queryOptions());
      const resp = await qc.ensureQueryData(
        trpcQcProxy.emailReport.getNotifications.queryOptions()
      );
      return { contacts: resp };
    },
  }
);

function RouteComponent() {
  const { trpcQcProxy: trpc } = Route.useRouteContext();
  const { contacts: initialContacts } = Route.useLoaderData();
  const { data: contacts } = useQuery(
    trpc.emailReport.getNotifications.queryOptions(undefined, {
      initialData: initialContacts,
    })
  );

  const { data: users } = useQuery(trpc.user.getAll.queryOptions());

  const [expanded, setExpanded] = useState(
    Object.values(IEmailReportEnum).reduce((acc, key) => {
      acc[key as IEmailReportEnum] = false;
      return acc;
    }, {} as { [P in IEmailReportEnum]: boolean })
  );

  const [addButtonClicked, setAddButtonClicked] = useState(
    Object.values(IEmailReportEnum).reduce((acc, key) => {
      acc[key as IEmailReportEnum] = false;
      return acc;
    }, {} as { [P in IEmailReportEnum]: boolean })
  );

  return (
    <Stack
      direction="column"
      sx={{
        width: "max-content",
      }}
    >
      <Button
        startIcon={
          Object.values(expanded).every((x) => !!x) ? (
            <ExpandLess />
          ) : (
            <ExpandMore />
          )
        }
        size="small"
        variant="text"
        sx={{ p: 1, m: 0 }}
        onClick={() => {
          setExpanded((prev) =>
            Object.values(IEmailReportEnum).reduce((acc, key) => {
              acc[key as IEmailReportEnum] = !Object.values(prev).every(
                (value) => value
              );
              return acc;
            }, {} as { [P in IEmailReportEnum]: boolean })
          );
        }}
      >
        {Object.values(expanded).every((x) => !!x) ? "Collapse" : "Expand"} All
      </Button>
      {Object.entries(IEmailReportEnumNameMap).map((item) => {
        const [key, value] = item as [
          keyof typeof IEmailReportEnumNameMap,
          string
        ];
        return (
          <Fragment key={key}>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <FormControlLabel
                control={
                  <IconButton
                    onClick={() => {
                      // button gets double clicked if there's no settimeout
                      setTimeout(() => {
                        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
                      }, 1);
                    }}
                  >
                    {expanded[key] ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                }
                label={value}
              />
              <IconButton
                onClick={() => {
                  setAddButtonClicked((prev) => ({
                    ...prev,
                    [key]: !prev[key],
                  }));
                }}
              >
                {addButtonClicked[key] ? <Close /> : <Add />}
              </IconButton>
            </Stack>
            <Collapse in={addButtonClicked[key]} timeout="auto" unmountOnExit>
              <EmailNotificationCreateSection
                emailType={key}
                users={users ?? []}
                onClose={() =>
                  setAddButtonClicked((prev) => ({
                    ...prev,
                    [key]: false,
                  }))
                }
                sx={{ px: { xs: 2, sm: 4 } }}
              />
            </Collapse>
            {expanded[key] && addButtonClicked[key] && (
              <Divider sx={{ my: 1 }} />
            )}
            {!!users && (
              <Collapse in={expanded[key]} timeout="auto" unmountOnExit>
                <EmailNotificationMoreInfoSection
                  notifications={contacts.filter(
                    (x) =>
                      x.emailType === key &&
                      (x.contact.inputType !== "user" ||
                        users.some((y) => y.id === x.contact.value))
                  )}
                  users={users}
                  sx={{
                    px: { xs: 2, sm: 4 },
                  }}
                />
              </Collapse>
            )}
          </Fragment>
        );
      })}
    </Stack>
  );
}
